import { getLocale } from '../stores/useLocaleStore';
import { getLocaleMeta } from 'i18n/locales';

// Memoized Intl.* factories.
//
// Intl constructors are expensive — measurably so when a 1,000-row table
// builds one per cell per render. Every formatter in the app funnels through
// here so the cost is paid once per (kind, tag, options) triple.
//
// Cache invalidation is not needed: entries are keyed by the resolved BCP-47
// tag, so switching language simply misses and constructs once more. The cache
// is unbounded by design — the key space is the handful of option shapes the
// app actually uses, not user input.

const cache = new Map();

// Resolve a locale argument to the tag Intl should use. Accepts:
//   - undefined  → the active UI locale
//   - 'es'       → a locale code from SUPPORTED_LOCALES
//   - 'de-DE'    → a raw BCP-47 tag, passed through (the decimal-convention
//                  override borrows de-DE/en-US purely for separator behaviour)
export const resolveIntlTag = (locale) => {
  if (!locale) return getLocaleMeta(getLocale()).intlTag;
  const meta = getLocaleMeta(locale);
  // getLocaleMeta falls back to the default locale for anything it does not
  // know; a hyphenated tag we do not ship is a deliberate raw tag, not a typo.
  return meta.code === locale ? meta.intlTag : locale;
};

const memo = (Ctor, kind) => (locale, options) => {
  const tag = resolveIntlTag(locale);
  const id = `${kind}|${tag}|${options ? JSON.stringify(options) : ''}`;
  let formatter = cache.get(id);
  if (!formatter) {
    formatter = new Ctor(tag, options);
    cache.set(id, formatter);
  }
  return formatter;
};

export const numberFormatter = memo(Intl.NumberFormat, 'n');
export const dateFormatter = memo(Intl.DateTimeFormat, 'd');
export const listFormatter = memo(Intl.ListFormat, 'l');
export const relativeFormatter = memo(Intl.RelativeTimeFormat, 'r');
export const displayNames = memo(Intl.DisplayNames, 'dn');
export const collator = memo(Intl.Collator, 'c');

// Test seam only — never call from application code.
export const __clearIntlCache = () => cache.clear();
